import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { ApiError, isApiError } from './apiError'
import { logError } from './logger'

interface ErrorMeta {
  silent?: boolean
  toastMessage?: string
  source?: string
}

function metaOf(meta: unknown): ErrorMeta {
  return (meta ?? {}) as ErrorMeta
}

function shouldToast(error: unknown, meta: ErrorMeta): boolean {
  if (meta.silent) return false
  if (isApiError(error)) {
    if (error.kind === 'canceled') return false
    if (error.kind === 'unauthorized') return false
    if (error.kind === 'validation') return false
  }
  return true
}

function messageFor(error: unknown, meta: ErrorMeta): string {
  if (meta.toastMessage) return meta.toastMessage
  if (isApiError(error)) return (error as ApiError).message
  if (error instanceof Error && error.message) return error.message
  return 'Something went wrong. Please try again.'
}

const queryCache = new QueryCache({
  onError: (error, query) => {
    const meta = metaOf(query.meta)
    logError(error, { source: meta.source ?? 'react-query:query', tags: { queryKey: JSON.stringify(query.queryKey) } })
    if (shouldToast(error, meta)) {
      toast.error(messageFor(error, meta))
    }
  },
})

const mutationCache = new MutationCache({
  onError: (error, _vars, _ctx, mutation) => {
    const meta = metaOf(mutation.meta)
    logError(error, { source: meta.source ?? 'react-query:mutation' })
    if (shouldToast(error, meta)) {
      toast.error(messageFor(error, meta))
    }
  },
})

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (isApiError(error) && !(error as ApiError).isRetryable) return false
        return failureCount < 2
      },
      retryDelay: (attempt, error) => {
        if (isApiError(error) && (error as ApiError).retryAfterMs !== undefined) {
          return Math.min((error as ApiError).retryAfterMs!, 30_000)
        }
        return Math.min(1000 * 2 ** attempt, 30_000)
      },
    },
    mutations: {
      retry: false,
    },
  },
})
